import { useTranslation } from "@refinedev/core";

import { Typography } from "@mui/material";

import { AlertEmailPreference } from "@/hooks/useAlertEmailPreference";

import { CoverageDialogShell, DEFAULT_COVERAGE_PREFIX, EmailPreferenceField } from "./coverage-dialog-parts";

export interface RemoveCoverageDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Translation key prefix containing the confirmRemove keys. */
  translationPrefix?: string;
  /** Name of the coverage practitioner being removed (for the body copy). */
  coveragePractitionerName: string;
  /**
   * Name of the covered practitioner — injected into the org-admin copy via
   * `{{coveredName}}`. Self-service strings use "your/you" and ignore it.
   */
  coveredPractitionerName?: string;
  /** Alert email preference chosen in the dialog (controlled). */
  emailPreference: AlertEmailPreference;
  onEmailPreferenceChange: (preference: AlertEmailPreference) => void;
  /** Additional confirm-disable condition (e.g. an in-flight lookup). */
  confirmDisabled?: boolean;
  /** Disable controls while the change is being saved. */
  isSaving?: boolean;
}

export const RemoveCoverageDialog = ({
  open,
  onConfirm,
  onCancel,
  translationPrefix = DEFAULT_COVERAGE_PREFIX,
  coveragePractitionerName,
  coveredPractitionerName,
  emailPreference,
  onEmailPreferenceChange,
  confirmDisabled = false,
  isSaving = false,
}: RemoveCoverageDialogProps) => {
  const { translate } = useTranslation();

  const prefix = `${translationPrefix}.confirmRemove`;
  // `name` = coverage practitioner being removed; `coveredName` = the covered practitioner.
  // escapeValue:false lets React handle escaping so names with apostrophes render correctly.
  const copyParams = {
    name: coveragePractitionerName,
    coveredName: coveredPractitionerName ?? "",
    interpolation: { escapeValue: false },
  };

  return (
    <CoverageDialogShell
      open={open}
      title={translate(`${prefix}.title`)}
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmLabel={translate(`${prefix}.confirm`)}
      confirmColor="error"
      confirmDisabled={confirmDisabled}
      isSaving={isSaving}
    >
      <Typography variant="body2" color="text.primary">
        {translate(`${prefix}.body`, copyParams)}
      </Typography>

      <EmailPreferenceField
        label={translate(`${prefix}.emailPrefDropdownLabel`)}
        description={translate(`${prefix}.emailPrefLabel`, copyParams)}
        value={emailPreference}
        onChange={onEmailPreferenceChange}
        disabled={isSaving}
      />
    </CoverageDialogShell>
  );
};
